import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import logoApp from '../assets/logo-app.png';
import './Sidebar.css';

const Sidebar = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { user, logout, hasPermission } = useAuth();

  const rawRole = String(user?.role || '').toUpperCase();
  const isTrial = rawRole === 'TRIAL';
  // TRIAL e UNITARIO são contas solo (sem time pra gerenciar) — herdam
  // permissão de nível GERENCIAL por baixo dos panos (é assim que
  // conseguem criar caso/ciclo/execução sozinhos), então os checks de
  // hasPermission abaixo dariam true pra UNITARIO também se não fosse por
  // esse bloqueio explícito. "Usuários e Funções" não faz sentido pra
  // nenhum dos dois: não tem ninguém além de si mesmo pra gerenciar.
  const isSoloAccount = isTrial || rawRole === 'UNITARIO';
  const canSeeSecuritySection = !isSoloAccount && (
    hasPermission('roles.read', ['ADM', 'ADMIN', 'GERENCIAL']) ||
    hasPermission('groups.read', ['ADM', 'ADMIN', 'GERENCIAL']) ||
    hasPermission('canCreateUser', ['ADM', 'ADMIN', 'GERENCIAL']) ||
    hasPermission('canEditUser', ['ADM', 'ADMIN', 'GERENCIAL'])
  );
  const [nowTs, setNowTs] = useState(Date.now());
  useEffect(() => {
    if (!isTrial) return undefined;
    const timer = setInterval(() => setNowTs(Date.now()), 30000);
    return () => clearInterval(timer);
  }, [isTrial]);
  const trialRemaining = useMemo(() => {
    if (!isTrial || !user?.created_at) return null;
    const createdAt = new Date(user.created_at);
    if (Number.isNaN(createdAt.getTime())) return null;
    const expiresAt = new Date(createdAt);
    expiresAt.setDate(expiresAt.getDate() + 15);
    const msLeft = expiresAt.getTime() - nowTs;
    if (msLeft <= 0) return { daysLeft: 0, label: 'expirado' };

    const dayMs = 1000 * 60 * 60 * 24;
    const daysLeft = Math.ceil(msLeft / dayMs);
    const totalMinutes = Math.floor(msLeft / (1000 * 60));
    const days = Math.floor(totalMinutes / (60 * 24));
    const hours = Math.floor((totalMinutes % (60 * 24)) / 60);
    const minutes = totalMinutes % 60;
    const label = days > 0
      ? `${days}d ${hours}h ${minutes}min`
      : (hours > 0 ? `${hours}h ${minutes}min` : `${minutes}min`);

    return { daysLeft, label };
  }, [isTrial, nowTs, user?.created_at]);

  // Rótulo por extenso pro cartão de perfil no rodapé (a chave curta tipo
  // "ADM"/"QA" já aparece em badge em outros lugares do app; aqui é texto
  // corrido mesmo, tipo "Administrador").
  const friendlyRoleLabel = (() => {
    switch (rawRole) {
      case 'ADM':
      case 'ADMIN': return 'Administrador';
      case 'GERENCIAL':
      case 'MANAGER': return 'Gerencial';
      case 'QA':
      case 'TESTER': return 'QA';
      case 'DEV':
      case 'DEVELOPER':
      case 'DESIGNER': return 'Desenvolvedor(a)';
      case 'TRIAL': return 'Avaliação';
      case 'UNITARIO': return 'Individual';
      case 'READONLY':
      case 'VIEWER': return 'Consulta';
      default: return rawRole || '';
    }
  })();
  const userInitials = `${(user?.first_name || user?.name || '?')[0] || ''}${(user?.last_name || '')[0] || ''}`.toUpperCase();

  const isActive = (path) => {
    if (!location || !location.pathname) return false;
    // Marca ativo quando a rota atual começa com o caminho do item
    return location.pathname === path || location.pathname.startsWith(path + '/');
  };

  const isMilestoneLinkingContext = (() => {
    if (!location || location.pathname !== '/executions') return false;
    const params = new URLSearchParams(location.search || '');
    return Boolean(params.get('milestone_id') && params.get('linking') === '1');
  })();

  return (
    <nav className="sidebar">
      <div className="sidebar-header">
        <div className="brand">
          <img className="brand-logo" src={logoApp} alt="QA'doro" />
        </div>
      </div>

      <ul className="nav-menu">
        <li className="menu-section">
          <ul className="submenu">
              <li className={isActive('/overview') ? 'active' : ''}>
                <button onClick={() => navigate('/overview')}>
                  <span className="menu-item-icon" aria-hidden="true">
                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round">
                      <path d="M3 10.5L12 3l9 7.5" />
                      <path d="M5.5 9.5V20a1 1 0 001 1H9v-6h6v6h2.5a1 1 0 001-1V9.5" />
                    </svg>
                  </span>
                  Visão Geral
                </button>
              </li>
              {hasPermission('canViewReports', ['ADM', 'ADMIN', 'GERENCIAL', 'DEV', 'QA']) && (
                <li className={isActive('/dashboard') ? 'active' : ''}>
                  <button onClick={() => navigate('/dashboard')}>
                    <span className="menu-item-icon" aria-hidden="true">
                      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round">
                        <path d="M4 21h16" />
                        <path d="M7 21v-6M12 21V9M17 21v-11" />
                      </svg>
                    </span>
                    Dashboard
                  </button>
                </li>
              )}
              {hasPermission('canManageProjects', ['ADM', 'ADMIN', 'GERENCIAL']) && (
                <li className={isActive('/projects') ? 'active' : ''}>
                  <button onClick={() => navigate('/projects')}>
                    <span className="menu-item-icon" aria-hidden="true">
                      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round">
                        <path d="M4 6.5A1.5 1.5 0 015.5 5h4l2 2h7A1.5 1.5 0 0120 8.5v9A1.5 1.5 0 0118.5 19h-13A1.5 1.5 0 014 17.5v-11z" />
                      </svg>
                    </span>
                    Projetos
                  </button>
                </li>
              )}
              {hasPermission('testcases.read', ['ADM', 'ADMIN', 'GERENCIAL', 'DEV', 'QA']) && (
                // Item ocultado temporariamente a pedido: manter módulo e rotas intactos
                null
              )}
              {hasPermission('testcases.read', ['ADM', 'ADMIN', 'GERENCIAL', 'DEV', 'QA']) && (
                <li className={isActive('/test-repository') ? 'active' : ''}>
                  <button onClick={() => navigate('/test-repository')}>
                    <span className="menu-item-icon" aria-hidden="true">
                      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round">
                        <rect x="6" y="4" width="12" height="17" rx="2" />
                        <path d="M9 4V3a1 1 0 011-1h4a1 1 0 011 1v1" />
                        <path d="M9 11.5l1.5 1.5L14 9.5" />
                        <path d="M9 16h6" />
                      </svg>
                    </span>
                    Repositório de Testes
                  </button>
                </li>
              )}
              {hasPermission('issues.read', ['ADM', 'ADMIN', 'GERENCIAL', 'DEV', 'QA']) && (
                <li className={isActive('/issues') ? 'active' : ''}>
                  <button onClick={() => navigate('/issues')}>
                    <span className="menu-item-icon" aria-hidden="true">
                      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round">
                        <circle cx="12" cy="6" r="2" />
                        <path d="M9.5 4.5L8 3M14.5 4.5L16 3" />
                        <rect x="7" y="9" width="10" height="11" rx="5" />
                        <path d="M12 9v11" />
                        <path d="M4 12h3M17 12h3M4 15h3M17 15h3M5 19l2.5-2M19 19l-2.5-2" />
                      </svg>
                    </span>
                    Bugs/Melhorias
                  </button>
                </li>
              )}
              {hasPermission('executions.read', ['ADM', 'ADMIN', 'GERENCIAL', 'DEV', 'QA']) && (
                <li className={(isActive('/executions') && !isMilestoneLinkingContext) ? 'active' : ''}>
                  <button onClick={() => navigate('/executions')}>
                    <span className="menu-item-icon" aria-hidden="true">
                      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round">
                        <circle cx="12" cy="12" r="8.5" />
                        <path d="M10 8.5l6 3.5-6 3.5v-7z" />
                      </svg>
                    </span>
                    Execuções
                  </button>
                </li>
              )}
              <li className={isActive('/integrations-v2') ? 'active' : ''}>
                <button onClick={() => navigate('/integrations-v2')}>
                  <span className="menu-item-icon" aria-hidden="true">
                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round">
                      <path d="M9.5 14.5l5-5" />
                      <path d="M13 5.5l1-1a3.2 3.2 0 014.5 4.5l-1 1" />
                      <path d="M11 18.5l-1 1a3.2 3.2 0 01-4.5-4.5l1-1" />
                    </svg>
                  </span>
                  Integrações
                </button>
              </li>
              {(((user?.role || '').toUpperCase() === 'ADM') || ((user?.role || '').toUpperCase() === 'ADMIN')) && (
                <li className={isActive('/status') ? 'active' : ''}>
                  <button onClick={() => navigate('/status')}>
                    <span className="menu-item-icon" aria-hidden="true">
                      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round">
                        <path d="M3 12h4l1.5-4.5L11 17l2-8 1.5 3H21" />
                      </svg>
                    </span>
                    Status
                  </button>
                </li>
              )}
          </ul>
        </li>

        {canSeeSecuritySection && (
          <li className="menu-section">
            <ul className="submenu">
              <li className={isActive('/user-management') ? 'active' : ''}>
                <button onClick={() => navigate('/user-management')}>
                  <span className="menu-item-icon" aria-hidden="true">
                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round">
                      <circle cx="12" cy="8" r="3.3" />
                      <path d="M5 20a7 7 0 0114 0" />
                    </svg>
                  </span>
                  Usuários e Funções
                </button>
              </li>
            </ul>
          </li>
        )}

      </ul>

      <div className="sidebar-footer">
        {isTrial && trialRemaining !== null && (
          <div
            className={`trial-expiry-badge ${trialRemaining.daysLeft <= 3 ? 'trial-expiry-badge--warning' : ''}`}
            title="Período de avaliação de 15 dias"
          >
            {trialRemaining.daysLeft > 0 ? `Trial expira em ${trialRemaining.label}` : 'Trial expirado'}
          </div>
        )}
        <div className="footer-user-row">
          <button
            type="button"
            className="footer-user-card"
            onClick={() => navigate('/meu-perfil')}
            title="Meu Perfil"
          >
            <div className="avatar" aria-hidden="true">
              {user?.avatar_url ? <img src={user.avatar_url} alt="" /> : (userInitials || '👤')}
            </div>
            <div className="user-details">
              <span className="user-name">{user.name}</span>
              <span className="user-role-label">{friendlyRoleLabel}</span>
            </div>
          </button>
          <button type="button" onClick={logout} className="logout-icon-btn" title="Sair">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
              <path d="M9 21H6a2 2 0 01-2-2V5a2 2 0 012-2h3" />
              <path d="M16 17l5-5-5-5" />
              <path d="M21 12H9" />
            </svg>
          </button>
        </div>
      </div>
    </nav>
  );
};

export default Sidebar;
